import { ReactElement } from 'react'
import {
    Bar,
    BarChart,
    Pie,
    PieChart,
    type PieLabelRenderProps,
    RadialBar,
    RadialBarChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis
} from 'recharts'
import { ParentAnswer, PollOptionResult, PollResults, RatingDisplay } from '@/types/NewsFeed'

const CHART_COLORS: string[] = [
    '#4d7eff',
    '#ff6b6b',
    '#ffd93d',
    '#6bcb77',
    '#c77dff',
    '#ff9f43',
    '#48dbfb',
    '#ff9ff3',
    '#54a0ff',
    '#5f27cd'
]

interface ChoiceChartProps {
    options: PollOptionResult[]
    totalAnswers: number
}

export function ChoiceBarChartViz({ options, totalAnswers }: Readonly<ChoiceChartProps>): ReactElement {
    const data: { name: string; count: number; pct: number }[] = options.map((opt: PollOptionResult) => ({
        name: opt.text || `Option ${opt.order + 1}`,
        count: opt.count,
        pct: totalAnswers > 0 ? Math.round((opt.count / totalAnswers) * 100) : 0
    }))

    return (
        <ResponsiveContainer width='100%' height={200}>
            <BarChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 8 }}>
                <XAxis dataKey='name' tick={{ fontSize: 12 }} />
                <YAxis allowDecimals={false} />
                <Tooltip
                    formatter={(value, _, props) => [
                        `${value} (${(props.payload as { pct?: number } | undefined)?.pct ?? 0}%)`,
                        'Votes'
                    ]}
                />
                <Bar dataKey='count' fill={CHART_COLORS[0]} />
            </BarChart>
        </ResponsiveContainer>
    )
}

export function ChoicePieChartViz({ options, totalAnswers }: Readonly<ChoiceChartProps>): ReactElement {
    const data: { name: string; value: number; pct: number; fill: string }[] = options.map(
        (opt: PollOptionResult, i: number) => ({
            name: opt.text || `Option ${opt.order + 1}`,
            value: opt.count,
            pct: totalAnswers > 0 ? Math.round((opt.count / totalAnswers) * 100) : 0,
            fill: CHART_COLORS[i % CHART_COLORS.length]
        })
    )

    return (
        <ResponsiveContainer width='100%' height={220}>
            <PieChart>
                <Pie
                    data={data}
                    dataKey='value'
                    nameKey='name'
                    cx='50%'
                    cy='50%'
                    outerRadius={80}
                    label={(props: PieLabelRenderProps) => `${(props as unknown as { pct: number }).pct}%`}
                />
                <Tooltip formatter={(value, name) => [value, name]} />
            </PieChart>
        </ResponsiveContainer>
    )
}

export function ChoiceRadialChartViz({ options }: Readonly<ChoiceChartProps>): ReactElement {
    const data: { name: string; value: number; fill: string }[] = options.map((opt: PollOptionResult, i: number) => ({
        name: opt.text || `Option ${opt.order + 1}`,
        value: opt.count,
        fill: CHART_COLORS[i % CHART_COLORS.length]
    }))

    return (
        <ResponsiveContainer width='100%' height={220}>
            <RadialBarChart cx='50%' cy='50%' innerRadius='20%' outerRadius='80%' data={data}>
                <RadialBar dataKey='value' label={{ position: 'insideStart', fill: '#fff', fontSize: 11 }} />
                <Tooltip formatter={(value, name) => [value, name]} />
            </RadialBarChart>
        </ResponsiveContainer>
    )
}

const SMILEY_CHARS: string[] = ['😣', '😞', '😐', '😊', '😄']

function buildRatingData(results: PollResults, display: RatingDisplay): { name: string; count: number }[] {
    return Array.from({ length: 5 }, (_: unknown, i: number) => {
        const rating: number = i + 1
        const count: number = results.parentAnswers
            ? results.parentAnswers.filter((a: ParentAnswer): boolean => a.rating === rating).length
            : (results.options.find((o: PollOptionResult): boolean => o.order === i)?.count ?? 0)
        const name: string =
            display === RatingDisplay.smileys
                ? SMILEY_CHARS[i]
                : display === RatingDisplay.stars
                  ? `★ ${rating}`
                  : String(rating)
        return { name, count }
    })
}

interface RatingChartProps {
    results: PollResults
    ratingDisplay: RatingDisplay
}

export function RatingBarChartViz({ results, ratingDisplay }: Readonly<RatingChartProps>): ReactElement {
    const data: { name: string; count: number }[] = buildRatingData(results, ratingDisplay)
    const maxCount: number = Math.max(...data.map((d: { count: number }): number => d.count), 1)
    const yTicks: number[] = Array.from({ length: maxCount + 1 }, (_: unknown, i: number): number => i)

    return (
        <ResponsiveContainer width='100%' height={200}>
            <BarChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 8 }}>
                <XAxis dataKey='name' />
                <YAxis allowDecimals={false} domain={[0, maxCount]} ticks={yTicks} />
                <Tooltip formatter={(value) => [value, 'Votes']} />
                <Bar dataKey='count' fill={CHART_COLORS[0]} />
            </BarChart>
        </ResponsiveContainer>
    )
}

export function RatingPieChartViz({ results, ratingDisplay }: Readonly<RatingChartProps>): ReactElement {
    const totalAnswers: number = results.totalAnswers
    type RatingDataPoint = { name: string; value: number; pct: number; fill: string }
    const data: RatingDataPoint[] = buildRatingData(results, ratingDisplay).map(
        (d: { name: string; count: number }, i: number): RatingDataPoint => ({
            name: d.name,
            value: d.count,
            pct: totalAnswers > 0 ? Math.round((d.count / totalAnswers) * 100) : 0,
            fill: CHART_COLORS[i]
        })
    )

    return (
        <ResponsiveContainer width='100%' height={220}>
            <PieChart>
                <Pie
                    data={data}
                    dataKey='value'
                    nameKey='name'
                    cx='50%'
                    cy='50%'
                    outerRadius={80}
                    label={(props: PieLabelRenderProps) => `${(props as unknown as { pct: number }).pct}%`}
                />
                <Tooltip formatter={(value, name) => [value, name]} />
            </PieChart>
        </ResponsiveContainer>
    )
}
